import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popconfirm, Tooltip } from 'antd';
import { CloseOutlined, FilePdfOutlined, UserAddOutlined, LogoutOutlined } from '@ant-design/icons';
import Avatar from './Avatar';
import conversationService from '../../services/conversationService';
import { T, derniereVueLibelle, tailleLisible } from './utils';

/** Colonne de droite : membres et pièces jointes. Repliable, masquée sous 1280 px. */
const Titre = ({ children }) => (
  <div style={{ fontSize: T.taille.meta, fontWeight: 600, color: T.muet, textTransform: 'uppercase', letterSpacing: 0.5, margin: '16px 16px 8px' }}>{children}</div>
);

export default function PanneauLateral({ conversation, surFermer, surAjouterMembres, surQuitter, moiTrackingId }) {
  const { t } = useTranslation('conversations');
  const [pieces, setPieces] = useState(null);
  const id = conversation?.conversationTrackingId;

  useEffect(() => {
    let actif = true;
    setPieces(null);
    if (!id) return undefined;
    conversationService.pieces(id).then((p) => actif && setPieces(p)).catch(() => actif && setPieces([]));
    return () => { actif = false; };
  }, [id, conversation?.derniereSequence]);

  if (!conversation) return null;
  const membres = conversation.membres.filter((m) => m.actif);
  const groupe = conversation.type !== 'DIRECT';

  return (
    <aside style={{ width: 300, flexShrink: 0, borderLeft: `1px solid ${T.bord}`, background: T.blanc, overflowY: 'auto', minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 8px 12px 16px', borderBottom: `1px solid ${T.bord}` }}>
        <span style={{ fontSize: T.taille.titre, fontWeight: 600 }}>{t('panneau.details')}</span>
        <button type="button" onClick={surFermer} aria-label={t('panneau.fermer')}
          style={{ width: 36, height: 36, border: 'none', background: 'none', cursor: 'pointer', color: T.muet }}>
          <CloseOutlined />
        </button>
      </div>

      <Titre>{t('panneau.membres', { n: membres.length })}</Titre>
      <div>
        {membres.map((m) => (
          <div key={m.utilisateurTrackingId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 16px' }}>
            <Avatar nom={m.nomComplet} initiales={m.initiales} taille={32} presence enLigne={m.enLigne} />
            <span style={{ minWidth: 0, flex: 1 }}>
              <span style={{ display: 'block', fontSize: T.taille.meta, fontWeight: 500, color: T.texte, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {m.nomComplet}{m.utilisateurTrackingId === moiTrackingId ? t('panneau.moi') : ''}
              </span>
              <span style={{ display: 'block', fontSize: T.taille.mini, color: T.muet }}>
                {m.profil ? `${m.profil} · ` : ''}{m.enLigne ? t('presence.enLigne') : derniereVueLibelle(m.derniereConnexion)}
              </span>
            </span>
            {m.role === 'ADMIN' && <span style={{ fontSize: 10, color: T.muet, border: `1px solid ${T.bord}`, borderRadius: 4, padding: '0 4px' }}>admin</span>}
          </div>
        ))}
      </div>
      {groupe && (
        <div style={{ display: 'flex', gap: 8, padding: '8px 16px' }}>
          <button type="button" onClick={surAjouterMembres}
            style={{ flex: 1, height: 36, border: `1px solid ${T.bord}`, borderRadius: T.rayon.champ, background: T.blanc, cursor: 'pointer', font: 'inherit', fontSize: T.taille.meta, color: T.texte2 }}>
            <UserAddOutlined /> {t('panneau.ajouter')}
          </button>
          <Popconfirm title={t('panneau.quitterConfirmation')} okText={t('panneau.quitter')} cancelText={t('panneau.annuler')} onConfirm={surQuitter}>
            <Tooltip title={t('panneau.quitter')}>
              <button type="button" aria-label={t('panneau.quitterConversation')}
                style={{ width: 36, height: 36, border: `1px solid ${T.bord}`, borderRadius: T.rayon.champ, background: T.blanc, cursor: 'pointer', color: T.rouge }}>
                <LogoutOutlined />
              </button>
            </Tooltip>
          </Popconfirm>
        </div>
      )}

      <Titre>{t('panneau.piecesJointes')}{pieces ? ` (${pieces.length})` : ''}</Titre>
      {pieces === null && <div style={{ padding: '0 16px', color: T.faible, fontSize: T.taille.meta }}>{t('panneau.chargement')}</div>}
      {pieces && pieces.length === 0 && <div style={{ padding: '0 16px 16px', color: T.muet, fontSize: T.taille.meta }}>{t('panneau.aucunePiece')}</div>}
      {pieces && pieces.length > 0 && (
        <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {pieces.map((p) => (
            <button key={p.pieceTrackingId} type="button" title={p.nomOriginal}
              onClick={async () => {
                const blob = await conversationService.lirePiece(p.pieceTrackingId);
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank', 'noopener');
                setTimeout(() => URL.revokeObjectURL(url), 60000);
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', minHeight: 36, border: `1px solid ${T.bord}`,
                borderRadius: T.rayon.champ, background: T.blanc, cursor: 'pointer', textAlign: 'left', font: 'inherit',
              }}>
              {p.image ? <span style={{ width: 20, height: 20, borderRadius: 4, background: T.vertPale, color: T.vert, fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>IMG</span>
                : <FilePdfOutlined style={{ color: '#b91c1c', fontSize: 18 }} />}
              <span style={{ flex: 1, minWidth: 0, fontSize: T.taille.mini, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.nomOriginal}</span>
              <span style={{ fontSize: T.taille.mini, color: T.muet }}>{tailleLisible(p.taille)}</span>
            </button>
          ))}
        </div>
      )}
    </aside>
  );
}
